import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { absoluteUrl } from "@/config/sites";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnswerFirst } from "@/components/AnswerFirst";
import { RECOMMEND_CLUSTERS, RECOMMEND_TOPICS, type RecommendClusterId } from "@/data/recommend/topics";
import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";

const TITLE = "แนะนำผู้ให้บริการแปล–รับรองเอกสาร–วีซ่า: เลือกอย่างไรไม่ให้เอกสารถูกตีกลับ";
const DESC =
  "คู่มือเลือกผู้ให้บริการแปลเอกสาร รับรองนิติกรณ์ และยื่นวีซ่า เกณฑ์ตรวจสอบใบอนุญาต คำถามที่ต้องถามก่อนจ้าง และลำดับการรับรองที่ถูกต้อง ปรึกษาทีมทนายก่อนเริ่มงาน";
const ANSWER =
  "เลือกผู้ให้บริการที่ตรวจสอบใบอนุญาตได้จริง ระบุลำดับการรับรองให้ตรงกับหน่วยงานปลายทาง แจ้งกรอบเวลาเป็นวันทำการ และไม่ให้คำมั่นเรื่องผลอนุมัติวีซ่าหรือการพิจารณาของราชการ เพราะผลเป็นดุลพินิจของหน่วยงานเท่านั้น สิ่งที่ผู้ให้บริการควบคุมได้คือความครบถ้วนและความถูกต้องของเอกสารก่อนยื่น";

export const Route = createFileRoute("/recommend/")({
  loader: () => {
    const url = absoluteUrl("/recommend");
    return {
      h: {
        meta: [
          { title: ctrTitle(TITLE) },
          { name: "description", content: ctrDesc(DESC) },
          { property: "og:title", content: ctrTitle(TITLE) },
          { property: "og:description", content: ctrDesc(DESC) },
          { property: "og:url", content: url },
          { property: "og:type", content: "article" },
          { name: "twitter:card", content: "summary_large_image" },
          { property: "og:image", content: SOCIAL_OG_IMAGE },
          { property: "og:image:alt", content: SOCIAL_OG_IMAGE_ALT },
          { name: "twitter:image", content: SOCIAL_OG_IMAGE },
        ],
        links: [{ rel: "canonical", href: url }],
        scripts: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: TITLE,
              itemListElement: RECOMMEND_TOPICS.map((t, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: t.h1,
                url: absoluteUrl(`/recommend/${t.cluster}/${encodeURIComponent(t.slug)}`),
              })),
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              url,
              name: TITLE,
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: ["h1", ".answer-summary", ".speakable-summary", ".lead"],
              },
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",

              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
                { "@type": "ListItem", position: 2, name: "คู่มือเลือกผู้ให้บริการ", item: url },
              ],
            }),
          },
        ],
      },
    };
  },
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: RecommendPillar,
});

const CHECKS: Array<{ q: string; d: string }> = [
  { q: "ใบอนุญาตตรวจสอบได้หรือไม่", d: "งานรับรองลายมือชื่อและเอกสารต้องทำโดยทนายความผู้ได้รับใบอนุญาตจากสภาทนายความ ขอเลขที่ใบอนุญาตและชื่อ-นามสกุลจริงเพื่อตรวจสอบได้ทุกครั้ง" },
  { q: "ระบุลำดับการรับรองให้ชัดไหม", d: "ผู้ให้บริการควรบอกได้ว่าเอกสารของคุณต้องผ่านกี่ขั้น เช่น คำรับรองทนาย → นิติกรณ์กรมการกงสุล → สถานทูตปลายทาง และขั้นใดที่ปลายทางของคุณไม่ต้องใช้" },
  { q: "แจ้งกรอบเวลาเป็นวันทำการหรือไม่", d: "กรอบเวลาต้องแยกส่วนที่ควบคุมได้ (แปล จัดชุดเอกสาร) ออกจากส่วนที่เป็นคิวของราชการ ซึ่งไม่มีผู้ให้บริการรายใดเร่งได้" },
  { q: "มีการรับประกันผลอนุมัติหรือไม่", d: "ถ้ามีคำโฆษณาว่าวีซ่าผ่านแน่นอน หรืออ้างเส้นสายกับหน่วยงาน ควรหลีกเลี่ยง เพราะขัดกับข้อเท็จจริงว่าการพิจารณาเป็นดุลพินิจของหน่วยงาน" },
  { q: "ค่าใช้จ่ายแยกค่าธรรมเนียมราชการชัดเจนไหม", d: "ใบเสนอราคาควรแยกค่าธรรมเนียมราชการตามประกาศ ออกจากค่าแปลและค่าบริการเดินเรื่อง เพื่อเทียบราคาระหว่างผู้ให้บริการได้อย่างเป็นธรรม" },
  { q: "รับผิดชอบเมื่อถูกตีกลับหรือไม่", d: "ควรระบุเงื่อนไขไว้ล่วงหน้าว่าหากหน่วยงานทักท้วงเรื่องรูปแบบคำแปลหรือการรับรอง ผู้ให้บริการจะแก้ไขและยื่นซ้ำให้โดยไม่มีค่าใช้จ่ายเพิ่มในส่วนที่เป็นความผิดพลาดของตน" },
];

function RecommendPillar() {
  const clusters = Object.keys(RECOMMEND_CLUSTERS) as RecommendClusterId[];
  return (
    <>
      <Breadcrumbs items={[{ label: "หน้าแรก", to: "/" }, { label: "คู่มือเลือกผู้ให้บริการ" }]} />
      <section className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-5">{TITLE}</h1>
        <AnswerFirst>{ANSWER}</AnswerFirst>

        <h2 className="text-2xl font-semibold mt-10 mb-4">6 ข้อที่ต้องถามก่อนจ้างผู้ให้บริการ</h2>
        <ol className="space-y-4">
          {CHECKS.map((c, i) => (
            <li key={c.q} className="rounded-xl border border-border p-4">
              <p className="font-semibold">
                {i + 1}. {c.q}
              </p>
              <p className="text-sm text-muted-foreground mt-1 faq-answer">{c.d}</p>
            </li>
          ))}
        </ol>

        <h2 className="text-2xl font-semibold mt-10 mb-4">คู่มือเลือกบริการแยกตามงานที่ต้องใช้</h2>
        {clusters.map((c) => {
          const topics = RECOMMEND_TOPICS.filter((t) => t.cluster === c);
          if (!topics.length) return null;
          return (
            <div key={c} className="mb-8">
              <h3 className="text-lg font-semibold">{RECOMMEND_CLUSTERS[c].label}</h3>
              <p className="text-sm text-muted-foreground mb-2">{RECOMMEND_CLUSTERS[c].blurb}</p>
              <ul className="space-y-1 text-sm">
                {topics.map((t) => (
                  <li key={t.slug}>
                    <Link
                      to="/recommend/$cluster/$topic"
                      params={{ cluster: t.cluster, topic: t.slug }}
                      className="text-blue-700 hover:underline"
                    >
                      {t.h1}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        <div className="mt-10 p-6 bg-muted rounded-xl">
          <p className="font-semibold">
            ปรึกษาทีมทนายก่อนเริ่มงาน โทร{" "}
            <a href="tel:0948958999" className="text-blue-700">
              094-895-8999
            </a>{" "}
            · LINE @Thainotary — เราตรวจเอกสารและแจ้งลำดับการรับรองที่ปลายทางของคุณต้องใช้ก่อนเสมอ
          </p>
        </div>
      </section>
    </>
  );
}
